/* eslint-disable no-use-before-define */
import {HISTORY_EVENT} from "../../config/constants"
import {useOnScrollEnd} from "../../hooks/useOnScrollEnd"
import {getEncodedValues, valuesEncoded} from "../../utils/getEncodedValues"
import {isSomewhatInViewport} from "../../utils/window"

export interface ScrolledPageInfo {
    /**
     * The page value when the count starts from one
     *
     * @remarks
     * For instance, if page 4 and 5 is loaded and you are viewing
     * page 4, then the `localPage` value will be 1. And if you are
     * viewing page 5, then the `localPage` value will be 2 etc.
     */
    localPage: number

    /**
     * The page being viewed
     *
     * @remarks
     * For instance, if page 4 to 8 is loaded and you are viewing page
     * 6, then the `actualPage` value will be 6
     */
    actualPage: number
}

interface ScrolledPageUrlTrackingProps {
    startPage: number
    itemsPerPage: number
    gridContainerElement: HTMLElement | null
    initialScrollCompleted: boolean
}

/**
 * Persists the page and scroll position to the
 * url as the page is scrolled
 */
export function useScrollPositionTracking({
    startPage,
    itemsPerPage,
    gridContainerElement,
    initialScrollCompleted,
}: ScrolledPageUrlTrackingProps) {
    useOnScrollEnd(() => {
        if (!gridContainerElement || !gridContainerElement.children.length || !initialScrollCompleted) return

        const scrolledPageInfo = helpers.getScrolledPageInfo(startPage, itemsPerPage, gridContainerElement)
        const scrollTopToPersist = helpers.calculateScrollTopToPersist(
            scrolledPageInfo,
            gridContainerElement,
            itemsPerPage,
        )
        if (scrollTopToPersist === null) return
        const replacementUrl = helpers.createReplacementUrl(scrolledPageInfo, scrollTopToPersist)

        helpers.replaceCurrentBrowserUrl(replacementUrl)
    }, [startPage, itemsPerPage, gridContainerElement, initialScrollCompleted])
}

export function getScrolledPageInfo(
    startPage: number,
    itemsPerPage: number,
    gridContainerElement: HTMLElement,
): ScrolledPageInfo {
    let actualPage = 0
    let localPage = 0

    const {children} = gridContainerElement

    for (let i = 0; i < children.length; i += 1) {
        const child = children[i]
        if (isSomewhatInViewport(child)) {
            const pagePos = Math.floor(i / itemsPerPage)
            localPage = pagePos + 1
            actualPage = startPage + pagePos
            break
        }
    }

    return {localPage, actualPage}
}

function calculateScrollTopToPersist(
    scrolledPageInfo: ScrolledPageInfo,
    gridContainerElement: HTMLElement,
    itemsPerPage: number,
) {
    if (!gridContainerElement.children.length) return null

    const {localPage} = scrolledPageInfo

    // Get the size of the gap between the scroll anchor and the top of the viewport
    const gapAtTopOfPage = document.querySelector("[data-scrollanchor='true']")?.getBoundingClientRect().bottom || 0

    // Use the last element of the local page to determine how much of the page is in the viewport
    const lastElementOfLocalPage = gridContainerElement.children[itemsPerPage * localPage - 1]
    if (!lastElementOfLocalPage) return null

    const lastElementOfLocalPageRectBottom = Math.round(lastElementOfLocalPage.getBoundingClientRect().bottom)

    // When the page boundary is in the viewport but hidden behind the gapAtTopOfPage,
    // it means the local page is not visible, so we return a negative value to indicate
    // that the replacement url should contain the next page
    if (lastElementOfLocalPageRectBottom <= gapAtTopOfPage && lastElementOfLocalPageRectBottom >= 0) {
        return -(gapAtTopOfPage - lastElementOfLocalPageRectBottom)
    }

    // The first page is treated differently because the scroll position is the same as the scrollY value
    // This comes after the above condition because the above handles when the page 1/page 2 boundary is hidden behind the gapAtTopOfPage
    if (localPage === 1) {
        return window.scrollY
    }

    // For other pages, use the position of the previous local page to find the top of the current local page
    // and calculate the scroll position of the local page as if it were the first page
    const lastElementOfPreviousLocalPage = gridContainerElement.children[itemsPerPage * (localPage - 1) - 1]
    if (!lastElementOfPreviousLocalPage) return null

    // the height of all previous local pages combined
    const previousLocalPagesHeight = lastElementOfPreviousLocalPage.getBoundingClientRect().bottom + window.scrollY

    // the scroll position of the local page as if it were the first page
    return window.scrollY - previousLocalPagesHeight + gapAtTopOfPage
}

function createReplacementUrl(scrolledPageInfo: ScrolledPageInfo, scrollTopToPersist: number) {
    const {actualPage} = scrolledPageInfo
    const baseUrlPath = window.location.href.split("#")[0]
    const url = new URL(baseUrlPath)

    // if the scrollTopToPersist is negative, it means the page boundary is hidden behind the gapAtTopOfPage so the visible page is page + 1
    const page = scrollTopToPersist < 0 || Object.is(scrollTopToPersist, -0) ? actualPage + 1 : actualPage

    const w = url.searchParams.get("w")
    if (w) url.searchParams.set("w", getEncodedValues(w, /&/gi, valuesEncoded))

    url.searchParams.set("p", page.toString())

    url.search = decodeQueryParam(url)

    // price filtered - need to decode the query
    const decodeUrl = `${url.toString()}#${Math.abs(scrollTopToPersist)}`

    return decodeUrl
}

function decodeQueryParam(url: URL) {
    const {searchParams} = url

    const p = searchParams.toString()
    const decodedString = decodeURIComponent(p.replace(/\+/g, " "))

    // change [] to %5b and %5d respectively in url
    if (searchParams.get("range")) {
        return getEncodedValues(decodedString, /\[|\]/gi, valuesEncoded)
    }

    return decodedString
}

function replaceCurrentBrowserUrl(url: string) {
    window.history.replaceState({url, type: HISTORY_EVENT}, "", url)
}

// To allow for the helper functions
// to be mocked out in tests
export const helpers = {
    getScrolledPageInfo,
    createReplacementUrl,
    replaceCurrentBrowserUrl,
    calculateScrollTopToPersist,
}
